import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, update, query, limitToLast } from 'firebase/database';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db, secondaryAuth } from './firebase';
import { logAdminAction } from './auditLog';
import logo from './assets/logo.png';
import PharmaciesRidersTab from './PharmaciesRidersTab';
import ComplianceTab from './ComplianceTab';
import BankAccountTab      from './BankAccountTab';
import RefundTab           from './RefundsTab';
import ReferralsTab        from './ReferralsTab';
import ReconciliationTab   from './ReconciliationTab';
import CRMTab              from './CRMTab';
import PhoneOrderTab       from './PhoneOrderTab';
import HRPayrollTab, { CreateWorkerForm } from './HRPayrollTab';
import MarketingTab from './MarketingTab';
import * as XLSX from 'xlsx';

if (localStorage.getItem('darkMode') === '1') {
  document.body.classList.add('dark-mode');
}

// ── STYLES ────────────────────────────────────────────────────────────────────
const s = {
  page:    { minHeight: '100vh', backgroundColor: '#F7F8FA', fontFamily: 'Calibri, Arial, sans-serif' },
  navbar:  { backgroundColor: '#0F1923', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64, position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 8px rgba(0,0,0,0.3)' },
  navTitle:{ fontSize: 17, fontWeight: 700, color: '#FFF', marginLeft: 10 },
  navRight:{ display: 'flex', alignItems: 'center', gap: 12 },
  content: { maxWidth: 1400, margin: '0 auto', padding: '24px 20px', display: 'flex', gap: 20, alignItems: 'flex-start' },
  sidebar: { width: 230, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, backgroundColor: '#FFF', borderRadius: 12, padding: 10, border: '1px solid #E5E7EB', position: 'sticky', top: 84, maxHeight: 'calc(100vh - 104px)', overflowY: 'auto' },
  sidebarItem: { padding: '10px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, backgroundColor: 'transparent', color: '#555', textAlign: 'left', width: '100%', display: 'flex', alignItems: 'center' },
  sidebarItemActive: { backgroundColor: '#1D9E75', color: '#FFF', fontWeight: 700 },
  mainPanel: { flex: 1, minWidth: 0 },
  // Kept for any leftover inline references — no longer used for the main nav.
  tabs:    { display: 'flex', gap: 4, marginBottom: 24, backgroundColor: '#FFF', borderRadius: 12, padding: 6, border: '1px solid #E5E7EB', flexWrap: 'wrap' },
  tab:     { padding: '9px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, backgroundColor: 'transparent', color: '#888', whiteSpace: 'nowrap' },
  tabActive:{ backgroundColor: '#1D9E75', color: '#FFF', fontWeight: 700 },
  card:    { backgroundColor: '#FFF', borderRadius: 12, padding: 20, border: '1px solid #E5E7EB', marginBottom: 16 },
  input:   { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 14, backgroundColor: '#F7F8FA', outline: 'none', boxSizing: 'border-box' },
  btn:     { padding: '11px 20px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  statCard:{ backgroundColor: '#FFF', borderRadius: 12, padding: 18, border: '1px solid #E5E7EB', textAlign: 'center' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:      { padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #E5E7EB', backgroundColor: '#F7F8FA', whiteSpace: 'nowrap' },
  td:      { padding: '10px 14px', borderBottom: '1px solid #F3F4F6', color: '#374151' },
};

// Roles mirror the same department codes workers get (see DEPARTMENTS in
// HRPayrollTab.js) so the two account systems use one shared vocabulary.
// 'support' was the old catch-all role — replaced by CS (its direct
// successor, same tab set) plus the rest of the departments, each scoped to
// only the tabs that department actually needs. superadmin is the only role
// with unrestricted access; the most sensitive tabs (Admin Accounts inside
// Users, Bank Account, Audit Log) stay superadmin-only regardless of role —
// see isSuperAdmin checks below, not ROLE_TABS.
const ROLES       = ['superadmin','FI','OP','MK','HR','IT','MG','CS','OT'];
const ROLE_LABELS = { superadmin:'Super Admin', FI:'Finance', OP:'Operations', MK:'Marketing', HR:'Human Resources', IT:'IT', MG:'Management', CS:'Customer Service', OT:'Other' };
const ROLE_COLORS = { superadmin:'#7C3AED', FI:'#1D9E75', OP:'#3B82F6', MK:'#F59E0B', HR:'#EC4899', IT:'#06B6D4', MG:'#8B5CF6', CS:'#F97316', OT:'#6B7280' };
const ROLE_TABS   = {
  superadmin: ['overview','financial','refunds','referrals','reconciliation','marketing','licenses','support','phone_orders','orders','users','crm','pharmacies_riders','compliance','hr_payroll','bank_account','audit_log'],
  FI: ['overview','financial','refunds','referrals','reconciliation','orders'],
  OP: ['overview','licenses','orders','pharmacies_riders','compliance'],
  MK: ['overview','marketing','crm'],
  // HR gets the Users tab (for Worker Accounts — see canManageHR below,
  // which hides the Admin Accounts module from anyone but superadmin) and
  // HR & Payroll itself, since account creation + attendance/leave/payroll
  // is literally this role's job.
  HR: ['overview','users','hr_payroll'],
  IT: ['overview','orders','support'],
  // Senior operational view — everything except the four superadmin-only
  // areas (Admin Accounts, HR & Payroll, Bank Account, Audit Log).
  MG: ['overview','financial','refunds','referrals','reconciliation','marketing','licenses','support','phone_orders','orders','crm','pharmacies_riders','compliance'],
  CS: ['support','orders','refunds','crm','phone_orders'],
  OT: ['overview'],
};
const TAB_LABELS = {
  overview:'📊 Overview', financial:'💰 Financial', refunds:'💸 Refunds', referrals:'🎁 Referrals',
  reconciliation:'🧮 Reconciliation',
  marketing:'📈 Marketing', licenses:'🏥 Licenses', support:'🎧 Support',
  phone_orders:'📞 Phone Orders', orders:'📦 All Orders', users:'👥 Users',
  crm:'🧑‍💼 CRM', pharmacies_riders:'🛡️ Pharmacies & Riders', compliance:'🔒 Compliance', hr_payroll:'🧾 HR & Payroll',
  bank_account:'🏦 Bank Account', audit_log:'📜 Audit Log',
};

function Badge({ text, bg, color }) {
  return <span style={{ backgroundColor: bg, color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{text}</span>;
}

// The Users tab's "Worker Accounts" module — reuses CreateWorkerForm from
// HRPayrollTab.js (the same component the HR & Payroll tab uses) instead of
// a second copy of the creation logic, so both entry points always behave
// identically. Pay rate, attendance, and payroll stay in HR & Payroll —
// this module is scoped to account creation and a quick status glance.
function WorkerAccountsModule({ onManage }) {
  const [workers, setWorkers] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    return onValue(ref(db, 'workers'), snap => {
      setWorkers(snap.exists() ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v })) : []);
    });
  }, []);

  const filteredWorkers = workers.filter(w =>
    !search ||
    w.name?.toLowerCase().includes(search.toLowerCase()) ||
    w.email?.toLowerCase().includes(search.toLowerCase()) ||
    w.phone?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <CreateWorkerForm source="Users → Worker Accounts" />
      <div style={s.card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, gap:12, flexWrap:'wrap' }}>
          <h3 style={{ fontWeight:700, margin:0, fontSize:15 }}>All Worker Accounts ({workers.length})</h3>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input
              style={{ ...s.input, width:220, padding:'7px 12px', fontSize:13 }}
              placeholder="Search workers..."
              value={search}
              onChange={e=>setSearch(e.target.value)}
            />
            <button style={{ padding:'7px 14px', border:'1px solid #E5E7EB', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600, backgroundColor:'#F7F8FA', color:'#1D9E75', whiteSpace:'nowrap' }} onClick={onManage}>
              🧾 Manage attendance & payroll →
            </button>
          </div>
        </div>
        {workers.length === 0 ? (
          <p style={{ color:'#888', fontSize:13, textAlign:'center', padding:'20px 0' }}>No worker accounts yet — create one above.</p>
        ) : filteredWorkers.length === 0 ? (
          <p style={{ color:'#888', fontSize:13, textAlign:'center', padding:'20px 0' }}>No workers match "{search}".</p>
        ) : (
          <table style={s.table}>
            <thead><tr>{['Name','Email','Phone','Hourly Rate','Status'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {filteredWorkers.map((w,i) => (
                <tr key={w.id} style={{ backgroundColor: w.suspended ? '#FFF5F5' : i%2===0?'#FFF':'#FAFAFA' }}>
                  <td style={{ ...s.td, fontWeight:600 }}>{w.name || '—'}</td>
                  <td style={s.td}>{w.email}</td>
                  <td style={s.td}>{w.phone || '—'}</td>
                  <td style={s.td}>GH₵ {(w.payRateGHS || 0).toFixed(2)}</td>
                  <td style={s.td}>
                    <Badge
                      text={w.suspended ? '🚫 Suspended' : w.isApproved ? '✅ Active' : '⏳ Pending'}
                      bg={w.suspended ? '#FEE2E2' : w.isApproved ? '#DCFCE7' : '#FEF3C7'}
                      color={w.suspended ? '#B91C1C' : w.isApproved ? '#15803D' : '#92400E'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// Matches the backend's own staleness gate (render-backend/index.js,
// RIDER_STALE_MS) — isOnline alone can lag reality for a while after a
// rider disconnects (the fix is a Firebase onDisconnect hook, which only
// covers *future* disconnects, plus this 10-minute lastSeen backstop for
// anything that slips through). Every "riders online" count in this
// dashboard should agree with what the dispatcher would actually use.
function isRiderReallyOnline(r) {
  return !!(r.isOnline && r.lastSeen && (Date.now() - new Date(r.lastSeen).getTime()) < 10 * 60 * 1000);
}

function exportToExcel(data, filename) {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  ws['!cols'] = Object.keys(data[0] || {}).map(() => ({ wch: 18 }));
  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0,10)}.xlsx`);
}

// ── LIVE MAP (Leaflet) ────────────────────────────────────────────────────────
function LiveNetworkMap({ pharmacies, pharmaciesMap, riders }) {
  const mapRef         = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef      = useRef({}); // 'pharmacy_<id>' | 'rider_<id>' -> L.Marker
  const [search, setSearch]     = useState('');
  const [searchError, setSearchError] = useState('');

  const approved = pharmacies.filter(p => p.status === 'approved' && !p.suspended);
  const online   = riders.filter(r => isRiderReallyOnline(r) && !r.suspended);

  const GHANA_CENTER = [7.9465, -1.0232];
  const GHANA_BOUNDS = [[4.5,-3.5],[11.5,1.5]];

  const CITIES = {
    'Greater Accra': [5.6037,-0.1870], 'Ashanti':[6.6885,-1.6244],
    'Western':[4.9016,-1.7800], 'Eastern':[6.1008,-0.4426],
    'Central':[5.5332,-1.0199], 'Northern':[9.4008,-0.8393],
    'Upper East':[10.7935,-0.8393], 'Upper West':[10.2529,-2.3247],
    'Volta':[6.9177,0.4337], 'Brong-Ahafo':[7.9465,-1.5990],
    'Oti':[8.0,0.5], 'Savannah':[9.5,-1.8],
    'Bono East':[7.8,-1.0], 'Ahafo':[6.9,-2.3],
    'Western North':[6.3,-2.7], 'North East':[10.5,-0.4],
  };

  const addMarkers = (map, L) => {
    map.eachLayer(layer => { if (layer instanceof L.Marker) map.removeLayer(layer); });
    markersRef.current = {};

    const pharmacyIcon = L.divIcon({
      html: `<div style="background:#1D9E75;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:16px;border:3px solid #FFF;box-shadow:0 2px 6px rgba(0,0,0,0.3)">💊</div>`,
      iconSize:[32,32], iconAnchor:[16,16], className:'',
    });
    const riderIcon = L.divIcon({
      html: `<div style="background:#3B82F6;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:16px;border:3px solid #FFF;box-shadow:0 2px 6px rgba(0,0,0,0.3)">🛵</div>`,
      iconSize:[32,32], iconAnchor:[16,16], className:'',
    });

    approved.forEach(p => {
      // pharmacies_map holds the real geocoded location from onboarding —
      // the plain `pharmacies` record never carries coordinates, so reading
      // lat/lng from there (as this used to) always fell through to a
      // random/region-jittered guess.
      const real = pharmaciesMap?.[p.id];
      let lat = real?.latitude, lng = real?.longitude;
      if (!lat || !lng) {
        const c = CITIES[p.region];
        if (c) { lat = c[0]+(Math.random()-0.5)*0.15; lng = c[1]+(Math.random()-0.5)*0.15; }
        else   { lat = GHANA_CENTER[0]+(Math.random()-0.5)*2; lng = GHANA_CENTER[1]+(Math.random()-0.5)*2; }
      }
      const marker = L.marker([lat,lng],{icon:pharmacyIcon}).addTo(map).bindPopup(
        `<div style="font-family:Arial,sans-serif;min-width:180px">
          <p style="font-weight:700;font-size:14px;margin:0 0 4px;color:#1D9E75">💊 ${p.pharmacyName}</p>
          <p style="font-size:12px;color:#555;margin:0 0 2px">📍 ${p.region||p.location||'Ghana'}</p>
          <p style="font-size:12px;color:#555;margin:0 0 6px">📞 ${p.phone||'—'}</p>
          <span style="font-size:11px;padding:2px 8px;border-radius:20px;background:${p.isOpen!==false?'#DCFCE7':'#FEE2E2'};color:${p.isOpen!==false?'#15803D':'#B91C1C'};font-weight:600">
            ${p.isOpen!==false?'🟢 Open':'🔴 Closed'}
          </span>
        </div>`
      );
      markersRef.current[`pharmacy_${p.id}`] = { marker, name: p.pharmacyName || '' };
    });

    online.forEach(r => {
      let lat = r.latitude, lng = r.longitude;
      if (!lat || !lng) { lat = GHANA_CENTER[0]+(Math.random()-0.5)*2; lng = GHANA_CENTER[1]+(Math.random()-0.5)*2; }
      const marker = L.marker([lat,lng],{icon:riderIcon}).addTo(map).bindPopup(
        `<div style="font-family:Arial,sans-serif;min-width:160px">
          <p style="font-weight:700;font-size:14px;margin:0 0 4px;color:#3B82F6">🛵 ${r.name||'Rider'}</p>
          <p style="font-size:12px;color:#555;margin:0 0 2px">📞 ${r.phone||'—'}</p>
          <p style="font-size:12px;color:#555;margin:0 0 6px">📦 ${r.totalDeliveries||0} deliveries</p>
          <span style="font-size:11px;padding:2px 8px;border-radius:20px;background:#DCFCE7;color:#15803D;font-weight:600">🟢 Online</span>
        </div>`
      );
      markersRef.current[`rider_${r.id}`] = { marker, name: r.name || '' };
    });
  };

  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css'; link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    const initMap = () => {
      if (!mapRef.current || mapInstanceRef.current) return;
      const L   = window.L;
      const map = L.map(mapRef.current, {
        center: GHANA_CENTER, zoom: 8,
        minZoom: 7,
        maxBounds: GHANA_BOUNDS,
        maxBoundsViscosity: 1.0,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors', maxZoom: 18,
      }).addTo(map);
      map.fitBounds(GHANA_BOUNDS);
      mapInstanceRef.current = map;
      addMarkers(map, L);
    };

    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = initMap;
      document.head.appendChild(script);
    } else { initMap(); }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mapInstanceRef.current && window.L) {
      addMarkers(mapInstanceRef.current, window.L);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacies, pharmaciesMap, riders]);

  const runSearch = (e) => {
    e.preventDefault();
    setSearchError('');
    const q = search.trim().toLowerCase();
    if (!q) return;
    const match = Object.values(markersRef.current).find(m => m.name.toLowerCase().includes(q));
    if (!match) { setSearchError(`No pharmacy or rider matching "${search}"`); return; }
    const latlng = match.marker.getLatLng();
    mapInstanceRef.current.setView(latlng, 14, { animate: true });
    match.marker.openPopup();
  };

  return (
    <div style={{ position:'relative' }}>
      <form onSubmit={runSearch} style={{ position:'absolute', top:12, left:12, zIndex:1000, display:'flex', gap:6 }}>
        <input
          style={{ padding:'8px 12px', borderRadius:8, border:'1px solid #E5E7EB', fontSize:13, width:220, outline:'none', boxShadow:'0 2px 6px rgba(0,0,0,0.15)' }}
          placeholder="🔍 Search rider or pharmacy..."
          value={search}
          onChange={e => { setSearch(e.target.value); setSearchError(''); }}
        />
        <button type="submit" style={{ padding:'8px 14px', backgroundColor:'#1D9E75', color:'#FFF', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:13, boxShadow:'0 2px 6px rgba(0,0,0,0.15)' }}>
          Search
        </button>
      </form>
      {searchError && (
        <div style={{ position:'absolute', top:54, left:12, zIndex:1000, backgroundColor:'#FEE2E2', color:'#B91C1C', padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:600 }}>
          {searchError}
        </div>
      )}
      <div ref={mapRef} style={{ width:'100%', height:320 }} />
      <div style={{ position:'absolute', bottom:12, right:12, backgroundColor:'rgba(255,255,255,0.95)', borderRadius:8, padding:'8px 12px', border:'1px solid #E5E7EB', zIndex:1000, fontSize:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
          <div style={{ width:14, height:14, borderRadius:'50%', backgroundColor:'#1D9E75' }} />
          <span style={{ color:'#555' }}>Pharmacy ({approved.length})</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ width:14, height:14, borderRadius:'50%', backgroundColor:'#3B82F6' }} />
          <span style={{ color:'#555' }}>Rider online ({online.length})</span>
        </div>
      </div>
    </div>
  );
}


// ── LOGIN ─────────────────────────────────────────────────────────────────────
function LoginPage() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try { await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password); }
    catch { setError('Invalid email or password.'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', backgroundColor:'#0F1923', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ backgroundColor:'#FFF', borderRadius:20, padding:40, maxWidth:420, width:'100%' }}>
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <img src={logo} alt="PharmDev" style={{ width:200, marginBottom:8 }} />
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0', fontWeight:600 }}>ADMIN · Authorised personnel only</p>
        </div>
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:600, marginBottom:6, color:'#444' }}>Email Address</label>
            <input style={s.input} type="email" placeholder="admin@pharmdev.com" value={email} onChange={e=>setEmail(e.target.value)} />
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:600, marginBottom:6, color:'#444' }}>Password</label>
            <input style={s.input} type="password" placeholder="Enter password" value={password} onChange={e=>setPassword(e.target.value)} />
          </div>
          {error && <div style={{ backgroundColor:'#FEE2E2', borderRadius:8, padding:10, marginBottom:12 }}><p style={{ color:'#B91C1C', fontSize:13, margin:0 }}>{error}</p></div>}
          <button type="submit" style={{ ...s.btn, width:'100%', opacity:loading?0.6:1 }} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In to Admin →'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── FINANCIAL TAB ─────────────────────────────────────────────────────────────
function FinancialTab({ orders, pharmacies, riders }) {
  const [period, setPeriod]           = useState('all');
  const [search, setSearch]           = useState('');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [activeSection, setActiveSection] = useState('summary');
  const [riderPayoutLogs, setRiderPayoutLogs]       = useState([]);
  const [pharmacyPayoutLogs, setPharmacyPayoutLogs] = useState([]);
  const [payoutAlerts, setPayoutAlerts]             = useState([]);

  // Real automated payouts, written by the daily 8pm job as
  // payouts/{date}/{riderId} and pharmacyPayouts/{date}/{pharmacyId}.
  const flattenPayoutsByDate = (val) =>
    Object.entries(val || {}).flatMap(([date, byRecipient]) =>
      Object.entries(byRecipient || {}).map(([recipientId, record]) => ({ date, recipientId, ...record }))
    ).sort((a, b) => new Date(b.processedAt) - new Date(a.processedAt));

  useEffect(() => {
    onValue(ref(db, 'payouts'), snap => setRiderPayoutLogs(flattenPayoutsByDate(snap.val())));
    onValue(ref(db, 'pharmacyPayouts'), snap => setPharmacyPayoutLogs(flattenPayoutsByDate(snap.val())));
    // The backend writes payoutAlerts/{date} when the daily run finds the
    // platform's Paystack balance too low to cover that day's payouts, and
    // removes it once a run succeeds — so any entry here is still live.
    onValue(ref(db, 'payoutAlerts'), snap => {
      const val = snap.val() || {};
      setPayoutAlerts(Object.entries(val).map(([date, a]) => ({ date, ...a })).sort((a, b) => b.date.localeCompare(a.date)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const now = new Date();
  const todayStr = now.toDateString();
  const weekAgo  = new Date(now - 7*24*60*60*1000);
  const monthAgo = new Date(now - 30*24*60*60*1000);

  const filterByPeriod = (list) => {
    let f = list;
    if (period==='today') f=f.filter(o=>new Date(o.createdAt).toDateString()===todayStr);
    else if (period==='week')  f=f.filter(o=>new Date(o.createdAt)>=weekAgo);
    else if (period==='month') f=f.filter(o=>new Date(o.createdAt)>=monthAgo);
    if (dateFrom) f=f.filter(o=>new Date(o.createdAt)>=new Date(dateFrom));
    if (dateTo)   f=f.filter(o=>new Date(o.createdAt)<=new Date(dateTo+'T23:59:59'));
    if (search)   f=f.filter(o=>(o.customerName||'').toLowerCase().includes(search.toLowerCase())||(o.orderId||o.id).toLowerCase().includes(search.toLowerCase()));
    return f;
  };

  // Pharmacy's true base amount (pre-commission-markup). Only fall back to
  // deriving it from a customer-facing total when pharmacySubtotal wasn't
  // recorded on the order (legacy data) — treating a marked-up customer
  // total as if it were already the base would double-count commission.
  const getPharmacyBase = (o) => {
    if (o.pharmacySubtotal != null) return o.pharmacySubtotal;
    const customerFacing = o.subtotal != null ? o.subtotal : (o.total - (o.deliveryFee||0) - (o.serviceFee||0));
    return (customerFacing || 0) / 1.30;
  };

  const delivered     = orders.filter(o=>o.status==='delivered');
  const periodOrders  = filterByPeriod(delivered);
  const totalRevenue  = periodOrders.reduce((s,o)=>s+(o.total||0),0);
  const totalSubtotal = periodOrders.reduce((s,o)=>s+getPharmacyBase(o),0);
  const totalDelivery = periodOrders.reduce((s,o)=>s+(o.deliveryFee||0),0);
  const totalService  = periodOrders.reduce((s,o)=>s+(o.serviceFee||0),0);
  const pharmdevComm  = totalSubtotal*0.30;
  const riderComm     = totalDelivery*0.80;
  const pharmdevDel   = totalDelivery*0.20;
  const totalPharmdev = pharmdevComm + pharmdevDel + totalService;
  const pharmacyPays  = totalSubtotal;
  const riderPays     = riderComm;

  const pharmacyBreakdown = (() => {
    const map = {};
    periodOrders.forEach(o => {
      const name = o.items?.[0]?.pharmacy||'Unknown';
      const pid  = o.items?.[0]?.pharmacyId||name;
      if (!map[pid]) map[pid]={ name, orders:0, revenue:0, subtotal:0, commission:0, owed:0, deliveryFees:0 };
      map[pid].orders++;
      map[pid].revenue     += o.total||0;
      map[pid].deliveryFees+= o.deliveryFee||0;
      const sub = getPharmacyBase(o);
      map[pid].subtotal  += sub;
      map[pid].commission+= sub*0.30;
      map[pid].owed      += sub;
    });
    return Object.values(map).sort((a,b)=>b.revenue-a.revenue);
  })();

  const riderBreakdown = (() => {
    const map = {};
    periodOrders.forEach(o => {
      if (!o.riderId||!o.riderName) return;
      if (!map[o.riderId]) map[o.riderId]={ name:o.riderName, deliveries:0, fees:0, earned:0, cut:0 };
      map[o.riderId].deliveries++;
      map[o.riderId].fees   += o.deliveryFee||0;
      map[o.riderId].earned += (o.deliveryFee||10)*0.80;
      map[o.riderId].cut    += (o.deliveryFee||10)*0.20;
    });
    return Object.values(map).sort((a,b)=>b.earned-a.earned);
  })();

  const SECTIONS = [
    {id:'summary',      label:'📊 Summary'},
    {id:'pharmacies',   label:'💊 Pharmacy Payouts'},
    {id:'riders',       label:'🛵 Rider Payouts'},
    {id:'transactions', label:'📋 Transactions'},
    {id:'payout_logs',  label:'🤖 Payout Logs'},
  ];

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ fontWeight:700, margin:0, fontSize:20 }}>💰 Financial Reports</h2>
          <p style={{ color:'#888', fontSize:13, margin:'4px 0 0' }}>Complete money flow — commissions, payouts, rider earnings</p>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <div style={{ display:'flex', backgroundColor:'#F1F3F5', borderRadius:10, padding:4 }}>
            {[{id:'today',label:'Today'},{id:'week',label:'Week'},{id:'month',label:'Month'},{id:'all',label:'All Time'}].map(p=>(
              <button key={p.id} style={{ padding:'7px 14px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:period===p.id?700:400, backgroundColor:period===p.id?'#FFF':'transparent', color:period===p.id?'#1D9E75':'#888' }} onClick={()=>setPeriod(p.id)}>{p.label}</button>
            ))}
          </div>
          <button style={{ ...s.btn, backgroundColor:'#1A1A2E', fontSize:13 }} onClick={()=>exportToExcel(periodOrders.map(o=>({'Order':(o.orderId||o.id).slice(-6).toUpperCase(),'Date':new Date(o.createdAt).toLocaleDateString(),'Customer':o.customerName||'','Total':o.total||0})),'Financial_Report')}>📥 Export Excel</button>
        </div>
      </div>

      {/* PAYOUT BALANCE ALERT */}
      {payoutAlerts.length > 0 && (
        <div style={{ backgroundColor:'#FEE2E2', border:'1px solid #FCA5A5', borderRadius:12, padding:16, marginBottom:20 }}>
          <p style={{ fontWeight:700, color:'#B91C1C', margin:'0 0 6px', fontSize:14 }}>🚨 Payout run skipped — Paystack balance too low</p>
          {payoutAlerts.map(a => (
            <p key={a.date} style={{ fontSize:13, color:'#B91C1C', margin:'2px 0' }}>
              {a.date}: needed GH₵ {a.required?.toFixed(2)}, only GH₵ {a.available?.toFixed(2)} available — top up the PharmDev Paystack balance so riders and pharmacies get paid.
            </p>
          ))}
        </div>
      )}

      {/* FLAGGED ORDERS ALERT — orders the backend's fraud check couldn't
          verify. These never get a rider or a payout until resolved here,
          so this is the only place staff can see and fix a wrongly-flagged
          real order instead of it silently sitting stuck forever. */}
      {orders.filter(o => o.priceVerified === false).length > 0 && (
        <div style={{ backgroundColor:'#FEF3C7', border:'1px solid #FCD34D', borderRadius:12, padding:16, marginBottom:20 }}>
          <p style={{ fontWeight:700, color:'#92400E', margin:'0 0 8px', fontSize:14 }}>
            ⚠️ {orders.filter(o => o.priceVerified === false).length} order(s) flagged by the payment/price check — blocked from rider assignment and payout until reviewed
          </p>
          {orders.filter(o => o.priceVerified === false).map(o => (
            <div key={o.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderTop:'1px solid #FDE68A', fontSize:13, gap:12, flexWrap:'wrap' }}>
              <div>
                <strong>#{(o.orderId || o.id).slice(-6).toUpperCase()}</strong> — {o.customerName || o.customerId || 'unknown customer'} — GH₵{(o.total||0).toFixed(2)} — {new Date(o.createdAt).toLocaleString()}
                {o.integrityFlag && (
                  <div style={{ color:'#92400E', fontSize:12, marginTop:2 }}>
                    declared GH₵{o.integrityFlag.declaredSubtotal ?? '—'} vs catalog GH₵{o.integrityFlag.realCustomerSubtotal ?? '—'}
                    {' · '}subtotal {o.integrityFlag.subtotalOk ? 'ok' : 'MISMATCH'}
                    {' · '}payment {o.integrityFlag.paymentOk ? 'ok' : 'NOT CONFIRMED'}
                  </div>
                )}
              </div>
              <button
                style={{ ...s.btn, backgroundColor:'#1D9E75', fontSize:12, padding:'6px 12px' }}
                onClick={() => {
                  if (!window.confirm(`Mark order #${(o.orderId || o.id).slice(-6).toUpperCase()} as verified? Only do this after confirming the customer really did pay GH₵${(o.total||0).toFixed(2)} for these items.`)) return;
                  update(ref(db, `orders/${o.id}`), { priceVerified: true, integrityFlag: null });
                }}
              >
                ✅ Mark Verified
              </button>
            </div>
          ))}
        </div>
      )}

      {/* SECTION TABS */}
      <div style={{ display:'flex', gap:4, marginBottom:20, backgroundColor:'#F1F3F5', borderRadius:10, padding:4, width:'fit-content', flexWrap:'wrap' }}>
        {SECTIONS.map(sec=>(
          <button key={sec.id} style={{ padding:'8px 16px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:activeSection===sec.id?700:400, backgroundColor:activeSection===sec.id?'#FFF':'transparent', color:activeSection===sec.id?'#1D9E75':'#888' }} onClick={()=>setActiveSection(sec.id)}>
            {sec.label}
          </button>
        ))}
      </div>

      {activeSection==='summary' && (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
            {[
              { label:'Total Revenue',       value:`GH₵ ${totalRevenue.toFixed(2)}`,  icon:'💰', color:'#1D9E75', sub:`${periodOrders.length} orders` },
              { label:'Medicine Sales',      value:`GH₵ ${totalSubtotal.toFixed(2)}`, icon:'💊', color:'#3B82F6', sub:'Customer paid prices' },
              { label:'Delivery Fees',       value:`GH₵ ${totalDelivery.toFixed(2)}`, icon:'🛵', color:'#F59E0B', sub:'Total delivery collected' },
              { label:'PharmDev Commission', value:`GH₵ ${totalPharmdev.toFixed(2)}`, icon:'🏦', color:'#7C3AED', sub:'30% medicine + 20% delivery + service fee' },
            ].map((st,i)=>(
              <div key={i} style={{ ...s.statCard, borderTop:`4px solid ${st.color}` }}>
                <p style={{ fontSize:24, margin:'0 0 8px' }}>{st.icon}</p>
                <p style={{ fontSize:22, fontWeight:800, color:st.color, margin:'0 0 4px' }}>{st.value}</p>
                <p style={{ fontSize:13, fontWeight:600, color:'#1A1A1A', margin:'0 0 4px' }}>{st.label}</p>
                <p style={{ fontSize:11, color:'#888', margin:0 }}>{st.sub}</p>
              </div>
            ))}
          </div>
          <div style={s.card}>
            <h3 style={{ fontWeight:700, marginBottom:16, fontSize:15 }}>💡 Money Flow</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div style={{ backgroundColor:'#F0FDF4', borderRadius:10, padding:16, border:'1px solid #BBF7D0' }}>
                <p style={{ fontWeight:700, fontSize:14, color:'#15803D', margin:'0 0 12px' }}>📥 PharmDev Income</p>
                {[['Medicine commission (30%)',pharmdevComm],['Delivery commission (20%)',pharmdevDel],['Service fee (100%)',totalService]].map(([l,v])=>(
                  <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'0.5px solid #BBF7D0' }}>
                    <span style={{ fontSize:13, color:'#555' }}>{l}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:'#15803D' }}>GH₵ {v.toFixed(2)}</span>
                  </div>
                ))}
                <div style={{ display:'flex', justifyContent:'space-between', borderTop:'2px solid #15803D', paddingTop:8, marginTop:8 }}>
                  <span style={{ fontSize:14, fontWeight:700 }}>Total Income</span>
                  <span style={{ fontSize:16, fontWeight:800, color:'#15803D' }}>GH₵ {totalPharmdev.toFixed(2)}</span>
                </div>
              </div>
              <div style={{ backgroundColor:'#FEF3C7', borderRadius:10, padding:16, border:'1px solid #FCD34D' }}>
                <p style={{ fontWeight:700, fontSize:14, color:'#92400E', margin:'0 0 12px' }}>📤 PharmDev Payouts</p>
                {[[`Pharmacy payouts (${pharmacyBreakdown.length} pharmacies)`,pharmacyPays],[`Rider payouts (${riderBreakdown.length} riders)`,riderPays]].map(([l,v])=>(
                  <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'0.5px solid #FCD34D' }}>
                    <span style={{ fontSize:13, color:'#555', flex:1, marginRight:8 }}>{l}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:'#92400E', whiteSpace:'nowrap' }}>GH₵ {v.toFixed(2)}</span>
                  </div>
                ))}
                <div style={{ display:'flex', justifyContent:'space-between', borderTop:'2px solid #92400E', paddingTop:8, marginTop:8 }}>
                  <span style={{ fontSize:14, fontWeight:700 }}>Total Payouts</span>
                  <span style={{ fontSize:16, fontWeight:800, color:'#92400E' }}>GH₵ {(pharmacyPays+riderPays).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeSection==='pharmacies' && (
        <div style={{ ...s.card, padding:0, overflow:'hidden' }}>
          <table style={s.table}>
            <thead><tr>{['Pharmacy','Orders','Medicine Sales','Commission (30%)','Pharmacy Gets','Delivery Fees'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {pharmacyBreakdown.map((p,i)=>(
                <tr key={i} style={{ backgroundColor:i%2===0?'#FFF':'#FAFAFA' }}>
                  <td style={{ ...s.td, fontWeight:700 }}>{p.name}</td>
                  <td style={s.td}>{p.orders}</td>
                  <td style={s.td}>GH₵ {p.subtotal.toFixed(2)}</td>
                  <td style={{ ...s.td, color:'#7C3AED', fontWeight:600 }}>GH₵ {p.commission.toFixed(2)}</td>
                  <td style={{ ...s.td, color:'#1D9E75', fontWeight:700 }}>GH₵ {p.owed.toFixed(2)}</td>
                  <td style={s.td}>GH₵ {p.deliveryFees.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeSection==='riders' && (
        <div style={{ ...s.card, padding:0, overflow:'hidden' }}>
          <table style={s.table}>
            <thead><tr>{['Rider','Deliveries','Fees Collected','Rider Gets (80%)','PharmDev (20%)'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {riderBreakdown.map((r,i)=>(
                <tr key={i} style={{ backgroundColor:i%2===0?'#FFF':'#FAFAFA' }}>
                  <td style={{ ...s.td, fontWeight:700 }}>{r.name}</td>
                  <td style={{ ...s.td, fontWeight:600, color:'#3B82F6' }}>{r.deliveries}</td>
                  <td style={s.td}>GH₵ {r.fees.toFixed(2)}</td>
                  <td style={{ ...s.td, fontWeight:700, color:'#1D9E75' }}>GH₵ {r.earned.toFixed(2)}</td>
                  <td style={{ ...s.td, color:'#7C3AED' }}>GH₵ {r.cut.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeSection==='transactions' && (
        <div>
          <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
            <input style={{ ...s.input, width:220 }} placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)} />
            <input type="date" style={{ ...s.input, width:140 }} value={dateFrom} onChange={e=>setDateFrom(e.target.value)} />
            <input type="date" style={{ ...s.input, width:140 }} value={dateTo}   onChange={e=>setDateTo(e.target.value)} />
            {(search||dateFrom||dateTo) && <button style={{ ...s.btn, backgroundColor:'#FEE2E2', color:'#B91C1C' }} onClick={()=>{setSearch('');setDateFrom('');setDateTo('');}}>✕</button>}
          </div>
          <div style={{ ...s.card, padding:0, overflow:'hidden' }}>
            <table style={s.table}>
              <thead><tr>{['Order','Date','Customer','Pharmacy','Total','PharmDev','Pharmacy Gets','Rider Gets'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {filterByPeriod(delivered).slice(0,200).map((o,i)=>{
                  const sub  = getPharmacyBase(o);
                  const comm = sub*0.30 + (o.deliveryFee||0)*0.20;
                  return (
                    <tr key={o.id} style={{ backgroundColor:i%2===0?'#FFF':'#FAFAFA' }}>
                      <td style={{ ...s.td, fontWeight:700, color:'#1D9E75' }}>#{(o.orderId||o.id).slice(-6).toUpperCase()}</td>
                      <td style={{ ...s.td, fontSize:12 }}>{new Date(o.createdAt).toLocaleDateString()}</td>
                      <td style={s.td}>{o.customerName||'—'}</td>
                      <td style={s.td}>{o.items?.[0]?.pharmacy||'—'}</td>
                      <td style={{ ...s.td, fontWeight:700 }}>GH₵ {(o.total||0).toFixed(2)}</td>
                      <td style={{ ...s.td, color:'#7C3AED', fontWeight:600 }}>GH₵ {comm.toFixed(2)}</td>
                      <td style={{ ...s.td, color:'#1D9E75', fontWeight:600 }}>GH₵ {sub.toFixed(2)}</td>
                      <td style={{ ...s.td, color:'#3B82F6', fontWeight:600 }}>GH₵ {((o.deliveryFee||10)*0.80).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSection==='payout_logs' && (
        <PayoutLogsSection
          riderLogs={riderPayoutLogs}
          pharmacyLogs={pharmacyPayoutLogs}
          riders={riders}
          pharmacies={pharmacies}
        />
      )}
    </div>
  );
}

// ── PAYOUT LOGS (real automated payouts from the 8pm daily job) ──────────────
function payoutStatusBadge(status) {
  const s2 = (status || '').toLowerCase();
  if (s2 === 'success') return <Badge text="✅ Success" bg="#DCFCE7" color="#15803D" />;
  if (s2 === 'failed')  return <Badge text="❌ Failed" bg="#FEE2E2" color="#B91C1C" />;
  return <Badge text={status || 'Unknown'} bg="#FEF3C7" color="#92400E" />;
}

function PayoutLogTable({ title, logs, nameLookup }) {
  const failedCount = logs.filter(l => (l.status || '').toUpperCase() === 'FAILED').length;
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <h3 style={{ fontWeight: 700, margin: 0, fontSize: 15 }}>{title} ({logs.length})</h3>
        {failedCount > 0 && <Badge text={`⚠️ ${failedCount} failed`} bg="#FEE2E2" color="#B91C1C" />}
      </div>
      {logs.length === 0 ? (
        <div style={{ ...s.card, textAlign: 'center', padding: 30 }}>
          <p style={{ color: '#888', fontSize: 13, margin: 0 }}>No payout runs recorded yet.</p>
        </div>
      ) : (
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <table style={s.table}>
            <thead><tr>{['Date','Recipient','Amount','Method','Status','Reference','Error'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={`${l.date}-${l.recipientId}`} style={{ backgroundColor: i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{l.date}</td>
                  <td style={{ ...s.td, fontWeight: 700 }}>{nameLookup(l.recipientId) || l.recipientId}</td>
                  <td style={{ ...s.td, fontWeight: 800, color: '#1D9E75' }}>GH₵ {(l.amount || 0).toFixed(2)}</td>
                  <td style={s.td}>{l.network ? l.network.toUpperCase() : '—'}{l.phone ? ` · ${l.phone}` : ''}</td>
                  <td style={s.td}>{payoutStatusBadge(l.status)}</td>
                  <td style={{ ...s.td, fontSize: 11, color: '#888' }}>{l.transferCode || '—'}</td>
                  <td style={{ ...s.td, fontSize: 12, color: '#B91C1C' }}>{l.error || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PayoutLogsSection({ riderLogs, pharmacyLogs, riders, pharmacies }) {
  const riderName    = (id) => riders.find(r => r.id === id)?.name;
  const pharmacyName = (id) => pharmacies.find(p => p.id === id)?.pharmacyName;

  return (
    <div>
      <p style={{ color: '#888', fontSize: 13, margin: '0 0 18px' }}>
        Real results from the automated 8pm daily payout job — every rider and pharmacy transfer attempt via Paystack, including failures (wrong MoMo number, missing payment account, insufficient balance, etc.).
      </p>
      <PayoutLogTable title="🛵 Rider Payouts" logs={riderLogs} nameLookup={riderName} />
      <PayoutLogTable title="💊 Pharmacy Payouts" logs={pharmacyLogs} nameLookup={pharmacyName} />
    </div>
  );
}

const AUDIT_ACTION_LABELS = {
  approve_pharmacy: '✅ Approved pharmacy', reject_pharmacy: '❌ Rejected pharmacy',
  suspend_pharmacy: '🚫 Suspended pharmacy', reactivate_pharmacy: '✅ Reactivated pharmacy',
  approve_rider: '✅ Approved rider', suspend_rider: '🚫 Suspended rider', reactivate_rider: '✅ Reactivated rider',
  process_refund: '↩ Processed refund', create_admin_user: '➕ Created admin user',
  suspend_admin_user: '🚫 Suspended admin user', activate_admin_user: '✅ Activated admin user',
};

function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    // Bounded to the most recent 500 entries — this log is append-only and
    // grows forever, so an unbounded live listener means every admin
    // session eventually re-downloads years of history on every reconnect.
    // Push keys sort chronologically, so limitToLast needs no extra index.
    onValue(query(ref(db, 'adminAuditLog'), limitToLast(500)), snap => {
      const val = snap.val() || {};
      setLogs(Object.values(val).sort((a, b) => new Date(b.performedAt) - new Date(a.performedAt)));
    });
  }, []);

  const filtered = logs.filter(l =>
    !search ||
    (l.target || '').toLowerCase().includes(search.toLowerCase()) ||
    (l.performedBy || '').toLowerCase().includes(search.toLowerCase()) ||
    (AUDIT_ACTION_LABELS[l.action] || l.action || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 18 }}>📜 Admin Audit Log</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>Who approved, suspended, refunded, or created what — and when</p>
        </div>
        <input
          style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, width: 260, outline: 'none', backgroundColor: '#F7F8FA' }}
          placeholder="Search action, target, or admin..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <div style={{ ...s.card, textAlign: 'center', padding: 40 }}>
          <p style={{ color: '#888', fontSize: 13, margin: 0 }}>No matching actions recorded yet.</p>
        </div>
      ) : (
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <table style={s.table}>
            <thead><tr>{['When', 'Action', 'Target', 'By', 'Details'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {filtered.slice(0, 300).map((l, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                  <td style={{ ...s.td, whiteSpace: 'nowrap', fontSize: 12 }}>{new Date(l.performedAt).toLocaleString()}</td>
                  <td style={{ ...s.td, fontWeight: 700 }}>{AUDIT_ACTION_LABELS[l.action] || l.action}</td>
                  <td style={s.td}>{l.target || '—'}</td>
                  <td style={{ ...s.td, fontSize: 12, color: '#555' }}>{l.performedBy}</td>
                  <td style={{ ...s.td, fontSize: 12, color: '#888' }}>
                    {l.details && Object.keys(l.details).length > 0
                      ? Object.entries(l.details).map(([k, v]) => `${k}: ${v}`).join(' · ')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── MAIN DASHBOARD ────────────────────────────────────────────────────────────
function AdminDashboard({ adminUser }) {
  const [activeTab, setActiveTab]     = useState('overview');
  const [orders, setOrders]           = useState([]);
  const [pharmacies, setPharmacies]   = useState([]);
  const [pharmaciesMap, setPharmaciesMap] = useState({});
  const [riders, setRiders]           = useState([]);
  const [users, setUsers]             = useState([]);
  const [adminUsers, setAdminUsers]   = useState([]);
  const [search, setSearch]           = useState('');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [newAdmin, setNewAdmin]       = useState({ email:'', password:'', role:'CS', name:'' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [darkMode, setDarkMode]       = useState(localStorage.getItem('darkMode')==='1');
  const [usersModule, setUsersModule] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const seenPendingLeaveIds = useRef(null); // null = baseline not established yet

  const role          = adminUser?.role || 'OT';
  const isSuperAdmin  = role === 'superadmin';
  const allowedTabs   = ROLE_TABS[role] || ROLE_TABS.OT;
  // HR manages worker accounts + attendance/leave/payroll, but can't touch
  // Admin Accounts, Bank Account, or the Audit Log — those stay
  // superadmin-only (see the Users tab and hr_payroll gate below).
  const canManageHR   = isSuperAdmin || role === 'HR';

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.body.classList.toggle('dark-mode', next);
    localStorage.setItem('darkMode', next ? '1' : '0');
  };

  // 45-minute pending-order reassignment used to run here client-side (and
  // duplicated in the pharmacy dashboard too, racing whichever admin/pharmacy
  // tab happened to be open). It's now handled authoritatively by the
  // render-backend's /orders/maintenance job, called every 5 minutes by
  // GitHub Actions, which also fixed a bug here where it wrote
  // `nextPharmacy.name` (undefined) instead of `nextPharmacy.pharmacyName`.

  useEffect(() => {
    onValue(ref(db,'orders'),     snap=>{
      const list = snap.exists()?Object.entries(snap.val()).map(([k,v])=>({id:k,...v})):[];
      setOrders(list);
    });
    onValue(ref(db,'pharmacies'), snap=>setPharmacies(snap.exists()?Object.entries(snap.val()).map(([k,v])=>({id:k,...v})):[]));
    onValue(ref(db,'pharmacies_map'), snap=>setPharmaciesMap(snap.val()||{}));
    onValue(ref(db,'riders'),     snap=>setRiders(snap.exists()?Object.entries(snap.val()).map(([k,v])=>({id:k,...v})):[]));
    onValue(ref(db,'users'),      snap=>setUsers(snap.exists()?Object.entries(snap.val()).map(([k,v])=>({id:k,...v})):[]));
    onValue(ref(db,'adminUsers'), snap=>setAdminUsers(snap.exists()?Object.entries(snap.val()).map(([k,v])=>({id:k,...v})):[]));
  }, []);

  // Live "push" notification for new leave requests. pharmdev-admin is a
  // plain web dashboard (not the Expo mobile apps), so this uses the
  // browser's own Notification API instead of Expo push — it fires a real
  // OS-level popup as long as this tab is open somewhere (any tab, not just
  // HR & Payroll), which is the closest equivalent without standing up Web
  // Push (VAPID keys + a service worker), a separate piece of infra nobody's
  // asked to invest in yet. The sidebar's 🌴 badge (next to HR & Payroll)
  // and the navbar badge above cover the case where notification permission
  // was never granted.
  useEffect(() => {
    if (!canManageHR) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    return onValue(ref(db,'leaveRequests'), snap => {
      const list = snap.exists() ? Object.entries(snap.val()).map(([id,v])=>({id,...v})) : [];
      setLeaveRequests(list);
      const pendingIds = new Set(list.filter(r=>r.status==='pending').map(r=>r.id));
      if (seenPendingLeaveIds.current === null) {
        seenPendingLeaveIds.current = pendingIds; // baseline — don't notify for requests already sitting there on load
        return;
      }
      const newOnes = list.filter(r => r.status==='pending' && !seenPendingLeaveIds.current.has(r.id));
      seenPendingLeaveIds.current = pendingIds;
      if (newOnes.length && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        newOnes.forEach(r => {
          new Notification('🌴 New leave request', {
            body: `${r.workerName} requested ${r.days} day${r.days===1?'':'s'} ${r.type} leave — "${r.reason||'no reason given'}"`,
          });
        });
      }
    });
  }, [canManageHR]);

  const now           = new Date();
  const thirtyMinsAgo = new Date(now - 30*60*1000);
  const delivered     = orders.filter(o=>o.status==='delivered');
  const delayedOrders = orders.filter(o=>o.status==='pending' && new Date(o.createdAt)<thirtyMinsAgo);
  const pendingLeaveCount = leaveRequests.filter(r=>r.status==='pending').length;
  const totalRevenue  = delivered.reduce((s,o)=>s+(o.total||0),0);
  // PharmDev's delivery-fee cut is 25% normally, or 20% when the rider also
  // earned the 5% speed bonus for delivering within 30 minutes — matches
  // riderShare() in PharmDevRider/App.js (rider keeps 75% base, or 80% with
  // the bonus; there's no late penalty anymore, only a withheld bonus).
  const totalComm     = delivered.reduce((s,o)=>s+((o.pharmacySubtotal||o.subtotal||(o.total-(o.deliveryFee||0)-(o.serviceFee||0))||0)*0.30)+((o.deliveryFee||0)*(o.riderPenalty?0.25:0.20))+(o.serviceFee||0),0);

  const approvePharmacy = async (p) => {
    if (!window.confirm(`Approve ${p.pharmacyName}?`)) return;
    await update(ref(db,`pharmacies/${p.id}`),{ status:'approved', approvedAt:new Date().toISOString() });
    await update(ref(db,`pharmacies_map/${p.id}`),{ name:p.pharmacyName, address:p.location, region:p.region, phone:p.phone, status:'approved', isOpen:true, rating:5.0, totalRatings:0, latitude:p.latitude||6.72, longitude:p.longitude||-1.59 });
    await logAdminAction('approve_pharmacy', p.pharmacyName, { pharmacyId: p.id });
    alert(`✅ ${p.pharmacyName} approved!`);
  };

  const rejectPharmacy = async (p) => {
    const reason = window.prompt(`Reason for rejecting ${p.pharmacyName}:`);
    if (!reason) return;
    await update(ref(db,`pharmacies/${p.id}`),{ status:'rejected', rejectedAt:new Date().toISOString(), rejectionReason:reason });
    await logAdminAction('reject_pharmacy', p.pharmacyName, { pharmacyId: p.id, reason });
  };

  const pushDelayAlert = async (order) => {
    await update(ref(db,`orders/${order.id}`),{ delayAlertSent:true, delayAlertAt:new Date().toISOString() });
    const pharmacyId = order.items?.[0]?.pharmacyId;
    if (pharmacyId) {
      await update(ref(db,`pharmacies/${pharmacyId}`),{ urgentAlert:{ message:`⚠️ URGENT: Order #${(order.orderId||order.id).slice(-6).toUpperCase()} has been pending over 30 minutes. Prepare immediately.`, sentAt:new Date().toISOString(), orderId:order.id } });
    }
    alert(`🚨 Delay alert sent!`);
  };

  const createAdminUser = async () => {
    if (!newAdmin.email||!newAdmin.password||!newAdmin.name) { alert('Fill all fields'); return; }
    setCreatingAdmin(true);
    try {
      // Use the secondary auth instance so this doesn't sign the acting
      // superadmin out of their own session.
      const cred = await createUserWithEmailAndPassword(secondaryAuth, newAdmin.email, newAdmin.password);
      await update(ref(db,`adminUsers/${cred.user.uid}`),{ uid:cred.user.uid, email:newAdmin.email, name:newAdmin.name, role:newAdmin.role, createdAt:new Date().toISOString(), active:true });
      await signOut(secondaryAuth);
      await logAdminAction('create_admin_user', newAdmin.email, { role: newAdmin.role, name: newAdmin.name });
      setNewAdmin({ email:'', password:'', role:'CS', name:'' });
      alert(`✅ Admin created: ${newAdmin.email}`);
    } catch(e) { alert(e.code==='auth/email-already-in-use'?'Email already in use':'Error'); }
    finally { setCreatingAdmin(false); }
  };

  const filteredOrders = orders.filter(o => {
    if (search && !(o.customerName||'').toLowerCase().includes(search.toLowerCase()) && !(o.orderId||o.id).toLowerCase().includes(search.toLowerCase())) return false;
    if (dateFrom && new Date(o.createdAt)<new Date(dateFrom)) return false;
    if (dateTo   && new Date(o.createdAt)>new Date(dateTo+'T23:59:59')) return false;
    return true;
  }).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt));

  return (
    <div style={s.page}>
      {/* NAVBAR */}
      <div style={s.navbar}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <button
            style={{ padding:'8px 10px', backgroundColor:'rgba(255,255,255,0.1)', color:'#FFF', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, cursor:'pointer', fontSize:16, lineHeight:1 }}
            onClick={()=>setSidebarOpen(v=>!v)}
            title={sidebarOpen ? 'Hide menu' : 'Show menu'}
          >
            ☰
          </button>
          <div style={{ backgroundColor:'#FFF', borderRadius:8, padding:'6px 12px', display:'flex', alignItems:'center' }}>
            <img src={logo} alt="PharmDev" style={{ height:38 }} />
          </div>
          <span style={s.navTitle}>Admin</span>
        </div>
        <div style={s.navRight}>
          <span style={{ fontSize:13, color:'#9CA3AF' }}>{adminUser?.email}</span>
          <span style={{ backgroundColor:ROLE_COLORS[role]||'#1D9E75', color:'#FFF', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700 }}>{ROLE_LABELS[role]||role}</span>
          {canManageHR && pendingLeaveCount>0 && (
            <span style={{ backgroundColor:'#F59E0B', color:'#FFF', borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:700 }} title="Pending leave requests">🌴 {pendingLeaveCount} Leave</span>
          )}
          {delayedOrders.length>0 && <span style={{ backgroundColor:'#EF4444', color:'#FFF', borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:700 }}>🔴 {delayedOrders.length} Delayed</span>}
          <button style={{ padding:'5px 12px', backgroundColor:'rgba(255,255,255,0.1)', color:'#FFF', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, cursor:'pointer', fontSize:16 }} onClick={toggleDarkMode}>
            {darkMode ? '☀️' : '🌙'}
          </button>
          <button style={{ padding:'6px 14px', backgroundColor:'rgba(255,255,255,0.1)', color:'#FFF', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, cursor:'pointer', fontSize:13 }} onClick={()=>signOut(auth)}>Log out</button>
        </div>
      </div>

      <div style={{ ...s.content, maxWidth: sidebarOpen ? 1400 : '100%' }}>
        {/* SIDEBAR */}
        {sidebarOpen && (
          <div style={s.sidebar}>
            {allowedTabs.map(tabId=>(
              <button key={tabId} style={{ ...s.sidebarItem, ...(activeTab===tabId?s.sidebarItemActive:{}) }} onClick={()=>setActiveTab(tabId)}>
                {TAB_LABELS[tabId]}
                {tabId==='licenses' && pharmacies.filter(p=>p.status==='pending').length>0 && (
                  <span style={{ marginLeft:'auto', backgroundColor:'#EF4444', color:'#FFF', borderRadius:20, padding:'1px 6px', fontSize:10, fontWeight:700 }}>{pharmacies.filter(p=>p.status==='pending').length}</span>
                )}
                {tabId==='hr_payroll' && pendingLeaveCount>0 && (
                  <span style={{ marginLeft:'auto', backgroundColor:'#F59E0B', color:'#FFF', borderRadius:20, padding:'1px 6px', fontSize:10, fontWeight:700 }}>{pendingLeaveCount}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* MAIN PANEL */}
        <div style={s.mainPanel}>

        {/* OVERVIEW */}
        {activeTab==='overview' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h2 style={{ fontWeight:700, margin:0, fontSize:20 }}>📊 Platform Overview</h2>
              <span style={{ fontSize:13, color:'#888' }}>Live · {now.toLocaleTimeString()}</span>
            </div>

            {/* LEAFLET MAP */}
            <div style={{ backgroundColor:'#FFF', borderRadius:12, border:'1px solid #E5E7EB', overflow:'hidden', marginBottom:20 }}>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <p style={{ fontWeight:700, fontSize:14, margin:0 }}>🗺️ Live PharmDev Network — Ghana</p>
                <div style={{ display:'flex', gap:16 }}>
                  <span style={{ fontSize:12, color:'#1D9E75', fontWeight:600 }}>💊 {pharmacies.filter(p=>p.status==='approved').length} pharmacies</span>
                  <span style={{ fontSize:12, color:'#3B82F6', fontWeight:600 }}>🛵 {riders.filter(isRiderReallyOnline).length} riders online</span>
                </div>
              </div>
              <LiveNetworkMap pharmacies={pharmacies} pharmaciesMap={pharmaciesMap} riders={riders} />
            </div>

            {/* STATS */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
              {[
                { label:'Total Orders',      value:orders.length,                                       color:'#1D9E75', icon:'📦', sub:`${orders.filter(o=>new Date(o.createdAt).toDateString()===now.toDateString()).length} today` },
                { label:'Active Pharmacies', value:pharmacies.filter(p=>p.status==='approved').length,  color:'#3B82F6', icon:'💊', sub:`${pharmacies.filter(p=>p.status==='pending').length} pending` },
                { label:'Riders',            value:riders.filter(r=>r.isApproved).length,               color:'#8B5CF6', icon:'🛵', sub:`${riders.filter(isRiderReallyOnline).length} online now` },
                { label:'Customers',         value:users.length,                                        color:'#F59E0B', icon:'👤', sub:'registered' },
                { label:"Today's Revenue",   value:`GH₵ ${orders.filter(o=>o.status==='delivered'&&new Date(o.createdAt).toDateString()===now.toDateString()).reduce((s,o)=>s+(o.total||0),0).toFixed(0)}`, color:'#10B981', icon:'💰', sub:'delivered orders' },
                { label:'Pending Orders',    value:orders.filter(o=>o.status==='pending').length,       color:'#F97316', icon:'⏳', sub:'awaiting pharmacy' },
                { label:'Pending Licenses',  value:pharmacies.filter(p=>p.status==='pending').length,   color:'#EF4444', icon:'🏥', sub:'need review' },
                { label:'Total Commission',  value:`GH₵ ${totalComm.toFixed(0)}`,                       color:'#7C3AED', icon:'🏦', sub:'all time' },
              ].map((st,i)=>(
                <div key={i} style={{ backgroundColor:'#FFF', borderRadius:12, padding:16, border:'1px solid #E5E7EB', borderTop:`4px solid ${st.color}` }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                    <span style={{ fontSize:24 }}>{st.icon}</span>
                    <span style={{ fontSize:11, color:'#888', textAlign:'right', lineHeight:1.4 }}>{st.sub}</span>
                  </div>
                  <p style={{ fontSize:28, fontWeight:800, color:st.color, margin:'0 0 4px' }}>{st.value}</p>
                  <p style={{ fontSize:13, color:'#555', margin:0, fontWeight:500 }}>{st.label}</p>
                </div>
              ))}
            </div>

            {/* RECENT ORDERS */}
            <div style={{ backgroundColor:'#FFF', borderRadius:12, border:'1px solid #E5E7EB', overflow:'hidden' }}>
              <div style={{ padding:'14px 16px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <p style={{ fontWeight:700, fontSize:14, margin:0 }}>📋 Recent Orders</p>
                <button style={{ fontSize:12, color:'#1D9E75', background:'none', border:'none', cursor:'pointer', fontWeight:600 }} onClick={()=>setActiveTab('orders')}>View all →</button>
              </div>
              <table style={s.table}>
                <thead><tr>{['Order','Customer','Pharmacy','Total','Status','Time'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {orders.sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)).slice(0,8).map((o,i)=>(
                    <tr key={o.id} style={{ backgroundColor:i%2===0?'#FFF':'#FAFAFA' }}>
                      <td style={{ ...s.td, fontWeight:700, color:'#1D9E75' }}>#{(o.orderId||o.id).slice(-6).toUpperCase()}</td>
                      <td style={s.td}>{o.customerName||'—'}</td>
                      <td style={s.td}>{o.items?.[0]?.pharmacy||'—'}</td>
                      <td style={{ ...s.td, fontWeight:600 }}>GH₵ {(o.total||0).toFixed(2)}</td>
                      <td style={s.td}><Badge text={o.status} bg={o.status==='delivered'?'#DCFCE7':o.status==='cancelled'?'#FEE2E2':'#FEF3C7'} color={o.status==='delivered'?'#15803D':o.status==='cancelled'?'#B91C1C':'#92400E'} /></td>
                      <td style={{ ...s.td, fontSize:12, color:'#888' }}>{new Date(o.createdAt).toLocaleTimeString('en-GH',{hour:'2-digit',minute:'2-digit'})}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab==='financial'         && <FinancialTab orders={orders} pharmacies={pharmacies} riders={riders} />}
        {activeTab==='refunds'           && <RefundTab orders={orders} />}
        {activeTab==='referrals'         && <ReferralsTab />}
        {activeTab==='reconciliation'    && <ReconciliationTab />}
        {activeTab==='crm'               && <CRMTab orders={orders} pharmacies={pharmacies} riders={riders} users={users} />}
        {activeTab==='phone_orders'      && <PhoneOrderTab pharmacies={pharmacies} orders={orders} users={users} />}
        {activeTab==='pharmacies_riders' && isSuperAdmin && <PharmaciesRidersTab />}
        {activeTab==='compliance' && <ComplianceTab orders={orders} />}
        {activeTab==='hr_payroll'        && canManageHR && <HRPayrollTab />}
        {activeTab==='bank_account'      && isSuperAdmin && <BankAccountTab />}
        {activeTab==='audit_log'         && isSuperAdmin && <AuditLogTab />}

        {/* LICENSES */}
        {activeTab==='licenses' && (
          <div>
            <h2 style={{ fontWeight:700, marginBottom:16 }}>🏥 License Verification</h2>
            {pharmacies.filter(p=>p.status==='pending').length===0 ? (
              <div style={{ ...s.card, textAlign:'center', padding:50 }}><p style={{ fontSize:36 }}>✅</p><p style={{ color:'#888', fontWeight:600 }}>No pending applications</p></div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))', gap:16 }}>
                {pharmacies.filter(p=>p.status==='pending').map(p=>(
                  <div key={p.id} style={{ ...s.card, borderTop:'4px solid #F59E0B' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
                      <div>
                        <p style={{ fontWeight:700, fontSize:16, margin:'0 0 4px' }}>{p.pharmacyName}</p>
                        <p style={{ fontSize:12, color:'#888', margin:0 }}>{p.email}</p>
                      </div>
                      <Badge text="⏳ Pending" bg="#FEF3C7" color="#92400E" />
                    </div>
                    <table style={{ width:'100%', fontSize:13 }}>
                      {[['Owner',p.ownerName],['Phone',p.phone],['Location',p.location],['Region',p.region],['License #',p.licenseNumber]].map(([l,v])=>(
                        <tr key={l}><td style={{ padding:'3px 0', color:'#888', width:80 }}>{l}</td><td style={{ padding:'3px 0', fontWeight:500 }}>{v||'—'}</td></tr>
                      ))}
                    </table>
                    <div style={{ display:'flex', gap:8, marginTop:14 }}>
                      <button style={{ flex:1, padding:'10px', backgroundColor:'#1D9E75', color:'#FFF', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer', fontSize:13 }} onClick={()=>approvePharmacy(p)}>✅ Approve</button>
                      <button style={{ flex:1, padding:'10px', backgroundColor:'#FEE2E2', color:'#B91C1C', border:'1px solid #FCA5A5', borderRadius:8, fontWeight:700, cursor:'pointer', fontSize:13 }} onClick={()=>rejectPharmacy(p)}>✕ Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SUPPORT */}
        {activeTab==='support' && (
          <div>
            <h2 style={{ fontWeight:700, marginBottom:8 }}>🎧 Customer Support</h2>
            <p style={{ color:'#888', marginBottom:20, fontSize:14 }}>Orders pending more than 30 minutes</p>
            {delayedOrders.length===0 ? (
              <div style={{ ...s.card, textAlign:'center', padding:50 }}><p style={{ fontSize:36 }}>✅</p><p style={{ fontWeight:600, color:'#888' }}>No delayed orders</p></div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))', gap:16 }}>
                {delayedOrders.map(order=>{
                  const mins = Math.floor((now-new Date(order.createdAt))/60000);
                  return (
                    <div key={order.id} style={{ ...s.card, borderTop:'4px solid #EF4444' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                        <p style={{ fontWeight:700, color:'#1D9E75', margin:0 }}>#{(order.orderId||order.id).slice(-6).toUpperCase()}</p>
                        <Badge text={`⏱ ${mins} min delayed`} bg="#FEE2E2" color="#B91C1C" />
                      </div>
                      <p style={{ fontSize:13, margin:'0 0 4px' }}>👤 {order.customerName}</p>
                      <p style={{ fontSize:13, margin:'0 0 4px' }}>💊 {order.items?.map(i=>i.name).join(', ')}</p>
                      <p style={{ fontSize:13, margin:'0 0 12px' }}>🏪 {order.items?.[0]?.pharmacy||'Unknown'}</p>
                      <button style={{ width:'100%', padding:'11px', backgroundColor:order.delayAlertSent?'#F7F8FA':'#EF4444', color:order.delayAlertSent?'#888':'#FFF', border:'none', borderRadius:8, fontWeight:700, cursor:order.delayAlertSent?'not-allowed':'pointer', fontSize:13 }}
                        onClick={()=>!order.delayAlertSent&&pushDelayAlert(order)} disabled={order.delayAlertSent}>
                        {order.delayAlertSent ? '✅ Alert Sent' : '🚨 Send Urgent Alert to Pharmacy'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ALL ORDERS */}
        {activeTab==='orders' && (
          <div>
            <h2 style={{ fontWeight:700, marginBottom:16 }}>📦 All Orders ({filteredOrders.length})</h2>
            <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
              <input style={{ ...s.input, flex:2, maxWidth:300 }} placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)} />
              <input type="date" style={{ ...s.input, width:160 }} value={dateFrom} onChange={e=>setDateFrom(e.target.value)} />
              <input type="date" style={{ ...s.input, width:160 }} value={dateTo}   onChange={e=>setDateTo(e.target.value)} />
              {(search||dateFrom||dateTo) && <button style={{ ...s.btn, backgroundColor:'#FEE2E2', color:'#B91C1C' }} onClick={()=>{setSearch('');setDateFrom('');setDateTo('');}}>✕</button>}
            </div>
            <div style={{ ...s.card, padding:0, overflow:'hidden' }}>
              <table style={s.table}>
                <thead><tr>{['Order','Customer','Pharmacy','Items','Total','Status','Date'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {filteredOrders.slice(0,100).map((o,i)=>(
                    <tr key={o.id} style={{ backgroundColor:i%2===0?'#FFF':'#FAFAFA' }}>
                      <td style={{ ...s.td, fontWeight:700, color:'#1D9E75' }}>#{(o.orderId||o.id).slice(-6).toUpperCase()}</td>
                      <td style={s.td}>{o.customerName||'—'}</td>
                      <td style={s.td}>{o.items?.[0]?.pharmacy||'—'}</td>
                      <td style={{ ...s.td, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{o.items?.map(i=>i.name).join(', ')||'—'}</td>
                      <td style={{ ...s.td, fontWeight:600 }}>GH₵ {(o.total||0).toFixed(2)}</td>
                      <td style={s.td}><Badge text={o.status} bg={o.status==='delivered'?'#DCFCE7':o.status==='cancelled'?'#FEE2E2':'#FEF3C7'} color={o.status==='delivered'?'#15803D':o.status==='cancelled'?'#B91C1C':'#92400E'} /></td>
                      <td style={{ ...s.td, fontSize:12, color:'#888' }}>{new Date(o.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* USERS */}
        {activeTab==='users' && canManageHR && (() => {
          // HR-role admins land straight on Worker Accounts — Admin
          // Accounts (which can grant dashboard access, including
          // superadmin) stays superadmin-only and isn't even offered as a
          // toggle option to anyone else.
          const effectiveModule = usersModule || (isSuperAdmin ? 'admins' : 'workers');
          return (
          <div>
            <h2 style={{ fontWeight:700, marginBottom:16 }}>👥 User Accounts</h2>

            {isSuperAdmin && (
              <div style={{ display:'flex', gap:4, marginBottom:20, backgroundColor:'#F1F3F5', borderRadius:10, padding:4, width:'fit-content' }}>
                {[
                  { id:'admins',  label:`🛡️ Admin Accounts (${adminUsers.length})` },
                  { id:'workers', label:'🧑‍💼 Worker Accounts' },
                ].map(m => (
                  <button key={m.id} style={{ padding:'8px 16px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:effectiveModule===m.id?700:400, backgroundColor:effectiveModule===m.id?'#FFF':'transparent', color:effectiveModule===m.id?'#1D9E75':'#888' }}
                    onClick={()=>setUsersModule(m.id)}>
                    {m.label}
                  </button>
                ))}
              </div>
            )}

            {effectiveModule==='admins' && isSuperAdmin && (
              <>
                <div style={s.card}>
                  <h3 style={{ fontWeight:700, marginBottom:16, fontSize:15 }}>➕ Create Admin Account</h3>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:12, marginBottom:12 }}>
                    <input style={s.input} placeholder="Full name"  value={newAdmin.name}     onChange={e=>setNewAdmin(a=>({...a,name:e.target.value}))} />
                    <input style={s.input} type="email" placeholder="Email" value={newAdmin.email} onChange={e=>setNewAdmin(a=>({...a,email:e.target.value}))} />
                    <input style={s.input} type="password" placeholder="Password" value={newAdmin.password} onChange={e=>setNewAdmin(a=>({...a,password:e.target.value}))} />
                    <select style={s.input} value={newAdmin.role} onChange={e=>setNewAdmin(a=>({...a,role:e.target.value}))}>
                      {ROLES.map(r=><option key={r} value={r}>{ROLE_LABELS[r]||r}</option>)}
                    </select>
                  </div>
                  <button style={{ ...s.btn, opacity:creatingAdmin?0.6:1 }} onClick={createAdminUser} disabled={creatingAdmin}>
                    {creatingAdmin?'Creating...':'✓ Create Admin Account'}
                  </button>
                </div>
                <div style={s.card}>
                  <h3 style={{ fontWeight:700, marginBottom:14, fontSize:15 }}>All Admin Accounts ({adminUsers.length})</h3>
                  <table style={s.table}>
                    <thead><tr>{['Name','Email','Role','Status','Created','Action'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {adminUsers.map((a,i)=>(
                        <tr key={a.id} style={{ backgroundColor:i%2===0?'#FFF':'#FAFAFA' }}>
                          <td style={{ ...s.td, fontWeight:600 }}>{a.name||'—'}</td>
                          <td style={s.td}>{a.email}</td>
                          <td style={s.td}><Badge text={ROLE_LABELS[a.role]||a.role} bg={(ROLE_COLORS[a.role]||'#888')+'20'} color={ROLE_COLORS[a.role]||'#888'} /></td>
                          <td style={s.td}><Badge text={a.active?'✅ Active':'🚫 Suspended'} bg={a.active?'#DCFCE7':'#FEE2E2'} color={a.active?'#15803D':'#B91C1C'} /></td>
                          <td style={{ ...s.td, fontSize:12, color:'#888' }}>{a.createdAt?new Date(a.createdAt).toLocaleDateString():'—'}</td>
                          <td style={s.td}>
                            {a.email!==auth.currentUser?.email && (
                              <button style={{ padding:'5px 12px', border:'none', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600, backgroundColor:a.active?'#FEE2E2':'#DCFCE7', color:a.active?'#B91C1C':'#15803D' }}
                                onClick={()=>{ if(window.confirm(`${a.active?'Suspend':'Activate'} ${a.name}?`)) { update(ref(db,`adminUsers/${a.id}`),{active:!a.active}); logAdminAction(a.active?'suspend_admin_user':'activate_admin_user', a.email); } }}>
                                {a.active?'🚫 Suspend':'✅ Activate'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {effectiveModule==='workers' && (
              <WorkerAccountsModule onManage={()=>setActiveTab('hr_payroll')} />
            )}
          </div>
          );
        })()}

        {/* MARKETING */}
        {activeTab==='marketing' && <MarketingTab orders={orders} pharmacies={pharmacies} riders={riders} users={users} />}
        </div>
      </div>
    </div>
  );
}

// ── ROOT ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser]         = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [checking, setChecking] = useState(true);

  // No public/unauthenticated routes on this app by design — recruitment
  // applications are handled entirely on pharmdev-website's own domain
  // (careers.html / apply.html), so this app never ships a code path that
  // renders anything before the admin login gate.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setUser(u);
      if (u) {
        onValue(ref(db,`adminUsers/${u.uid}`), snap => {
          setAdminData(snap.exists() ? {...snap.val(), email:u.email} : {email:u.email, role:'superadmin'});
          setChecking(false);
        });
      } else { setAdminData(null); setChecking(false); }
    });
    return () => unsub();
  }, []);

  if (checking) return (
    <div style={{ minHeight:'100vh', backgroundColor:'#0F1923', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <p style={{ color:'#FFF', fontSize:16 }}>Loading...</p>
    </div>
  );

  if (!user) return <LoginPage />;
  return <AdminDashboard adminUser={adminData} />;
}